import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  FaCheck,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaComments,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaShieldAlt,
  FaStar,
  FaTruck,
} from "react-icons/fa";
import { toast } from "react-toastify";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import { useProductDetailsMaster } from "../../hooks/useProductDetailsMaster";
import b2bHubHomeService from "../../services/b2bHubHome.service";
import CommonService from "../../services/common.service";
import StorageService from "../../services/StorageService";
import { resolveHubImage } from "../../utils/b2bHubHomeMedia";
import { getMarketplaceEngagementNotice } from "../../utils/marketplaceProductEngagement";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/product_marketplace_checkout.scss";

const CHECKOUT_STEPS = [
  { id: "review", label: "Product Review" },
  { id: "address", label: "Delivery Address" },
  { id: "shipping", label: "Shipping" },
  { id: "payment", label: "Payment" },
  { id: "confirmation", label: "Confirmation" },
];

const SHIPPING_OPTIONS = [
  {
    id: "standard",
    title: "Standard Shipping",
    description: "Economy delivery via road or rail. Best value for bulk B2B orders.",
    price: 0,
    etaDays: 7,
  },
  {
    id: "express",
    title: "Express Shipping",
    description: "Priority dispatch with expedited courier handling.",
    price: 499,
    etaDays: 3,
  },
  {
    id: "pickup",
    title: "Self Pickup",
    description: "Collect from supplier warehouse. No freight charges apply.",
    price: 0,
    etaDays: 1,
  },
];

const RECENTLY_VIEWED_KEY = "trocanet_recently_viewed_products";
const GST_RATE = 0.18;

const parsePriceNumber = (value) => {
  const n = parseFloat(String(value ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const formatInr = (amount) => {
  if (amount == null || !Number.isFinite(amount)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount);
};

const addDaysLabel = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const pushRecentlyViewed = (product) => {
  if (!product?.id) return;
  try {
    const raw = sessionStorage.getItem(RECENTLY_VIEWED_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const entry = {
      id: product.id,
      title: product.title,
      image_path: product.image_path || product.gallery_images?.[0] || null,
      price_label: product.price_label || product.rate_label || null,
    };
    const next = [entry, ...list.filter((x) => x.id !== product.id)].slice(0, 12);
    sessionStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(next));
  } catch {
    /* ignore storage errors */
  }
};

const readRecentlyViewed = (excludeId) => {
  try {
    const raw = sessionStorage.getItem(RECENTLY_VIEWED_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return list.filter((x) => x.id !== excludeId);
  } catch {
    return [];
  }
};

function ProductCarousel({ title, items, trackId }) {
  if (!items?.length) return null;
  return (
    <section className="mpc-carousel-section">
      <div className="mpc-carousel-section__head">
        <h2>{title}</h2>
        <div className="mpc-carousel-section__nav">
          <button
            type="button"
            aria-label="Scroll left"
            onClick={() => document.getElementById(trackId)?.scrollBy({ left: -280, behavior: "smooth" })}
          >
            <FaChevronLeft aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Scroll right"
            onClick={() => document.getElementById(trackId)?.scrollBy({ left: 280, behavior: "smooth" })}
          >
            <FaChevronRight aria-hidden />
          </button>
        </div>
      </div>
      <div className="mpc-carousel-section__track" id={trackId}>
        {items.map((item) => (
          <Link key={item.id} to={`/marketplace/products/${item.id}`} className="mpc-product-card">
            <div className="mpc-product-card__media">
              {item.image_path ? (
                <img src={resolveHubImage(item.image_path)} alt={item.title} loading="lazy" />
              ) : (
                <div className="mpc-product-card__media" style={{ display: "grid", placeItems: "center", color: "#94a3b8" }}>
                  No image
                </div>
              )}
            </div>
            <div className="mpc-product-card__body">
              <h4>{item.title}</h4>
              {item.price_label ? <p className="mpc-product-card__price">{item.price_label}</p> : null}
              <span className="mpc-product-card__cta">View product</span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function ProductMarketplaceCheckoutPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { attributeUnitOptions } = useProductDetailsMaster();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [productEngagement, setProductEngagement] = useState(null);
  const [activeStep, setActiveStep] = useState("review");

  const [activeImage, setActiveImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [selectedAddressId, setSelectedAddressId] = useState("primary");
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [newAddress, setNewAddress] = useState({
    label: "Office",
    line: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
  });
  const [gstNumber, setGstNumber] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [poReference, setPoReference] = useState("");
  const [specialInstructions, setSpecialInstructions] = useState(() => searchParams.get("message") || "");
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  const sectionRefs = {
    review: useRef(null),
    address: useRef(null),
    shipping: useRef(null),
    payment: useRef(null),
  };

  useEffect(() => {
    if (!StorageService.getToken()) {
      toast.info("Please sign in to checkout");
      navigate("/login", { state: { from: location.pathname + location.search } });
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setProductEngagement(null);

    b2bHubHomeService
      .getProductDetail(productId)
      .then((res) => {
        if (cancelled) return;
        const payload =
          res?.success !== false && res?.data?.product
            ? res.data
            : res?.product
              ? res
              : null;
        if (!payload?.product) {
          setData(null);
          return;
        }
        setData(payload);
        pushRecentlyViewed(payload.product);
        setRecentlyViewed(readRecentlyViewed(payload.product.id));

        const moq = Math.max(1, parseInt(String(payload.product.moq || 1), 10) || 1);
        const invRows = payload.product.inventory_rows || [];
        const availValues = invRows
          .map((r) => Number(r.available_quantity))
          .filter((n) => Number.isFinite(n) && n >= 0);
        const availMax = availValues.length ? Math.max(...availValues) : null;
        setQuantity(availMax != null ? Math.min(moq, availMax) : moq);

        const colorParam = searchParams.get("color");
        const colors = payload.product.colour_options || [];
        if (colorParam && colors.some((c) => String(c.id) === colorParam)) {
          setSelectedColor(colorParam);
        } else {
          setSelectedColor(colors[0]?.id ?? null);
        }

        if (payload.buyer_engagement?.has_submitted) {
          setProductEngagement(payload.buyer_engagement);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("Failed to load product for checkout");
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [productId, searchParams]);

  useEffect(() => {
    let cancelled = false;
    if (!StorageService.getToken()) return undefined;

    CommonService.get("/company/my-profile", {}, { silent: true })
      .then((res) => {
        if (cancelled || !res?.success || !res?.data) return;
        const profile = res.data;
        setCompanyName(profile.company_name || profile.name || "");
        setGstNumber(profile.gstin || profile.gst_number || profile.tax_id || "");

        const addresses = [];
        const addr = profile.address;
        if (addr?.address || addr?.city) {
          addresses.push({
            id: "primary",
            label: "Registered Address",
            isDefault: true,
            line: [addr.address, addr.landmark].filter(Boolean).join(", "),
            city: addr.city || "",
            state: addr.state || "",
            pincode: addr.pincode || "",
            country: addr.country || "India",
          });
        }
        const overseas = profile.overseas_address;
        if (overseas?.address) {
          addresses.push({
            id: "overseas",
            label: "Overseas Address",
            isDefault: false,
            line: overseas.address,
            city: overseas.city || "",
            state: overseas.state || "",
            pincode: overseas.pincode || "",
            country: overseas.country || "",
          });
        }
        setSavedAddresses(addresses);
        if (addresses.length) setSelectedAddressId(addresses[0].id);
      })
      .catch(() => {
        /* profile optional */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const product = data?.product;
  const supplier = data?.supplier;
  const related = data?.related_products || [];
  const breadcrumb = data?.breadcrumb || [];

  const moqUnitLabel = useMemo(() => {
    const raw = product?.moq_unit;
    if (!raw) return "pieces";
    const match = attributeUnitOptions.find((o) => String(o.value) === String(raw));
    return match?.label || raw;
  }, [product?.moq_unit, attributeUnitOptions]);

  const moqMin = useMemo(() => {
    const n = parseInt(String(product?.moq || 1), 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }, [product?.moq]);

  const galleryImages = useMemo(() => {
    const list = product?.gallery_images || [];
    return list.length ? list : product?.image_path ? [product.image_path] : [];
  }, [product]);

  const availableQty = useMemo(() => {
    const rows = product?.inventory_rows || [];
    const values = rows
      .map((r) => Number(r.available_quantity))
      .filter((n) => Number.isFinite(n) && n >= 0);
    if (!values.length) return null;
    return Math.max(...values);
  }, [product?.inventory_rows]);

  const quantityMax = useMemo(() => {
    if (availableQty == null || !Number.isFinite(availableQty)) return null;
    return availableQty;
  }, [availableQty]);

  const stockBelowMoq = quantityMax != null && quantityMax < moqMin;

  const clampQuantity = useCallback(
    (value) => {
      let next = Math.max(moqMin, Math.floor(Number(value) || moqMin));
      if (quantityMax != null) next = Math.min(quantityMax, next);
      return next;
    },
    [moqMin, quantityMax],
  );

  useEffect(() => {
    if (!product) return;
    setQuantity((q) => clampQuantity(q));
  }, [product?.id, moqMin, quantityMax, clampQuantity, product]);

  const unitPrice = useMemo(() => {
    const fromProduct = parsePriceNumber(product?.price_amount);
    if (fromProduct != null) return fromProduct;
    return parsePriceNumber(product?.rate_label || product?.price_label);
  }, [product]);

  const selectedShipping = SHIPPING_OPTIONS.find((o) => o.id === shippingMethod) || SHIPPING_OPTIONS[0];

  const selectedAddress = useMemo(() => {
    if (selectedAddressId === "new") {
      return {
        label: newAddress.label,
        line: newAddress.line,
        city: newAddress.city,
        state: newAddress.state,
        pincode: newAddress.pincode,
        country: newAddress.country,
      };
    }
    return savedAddresses.find((a) => a.id === selectedAddressId) || savedAddresses[0] || null;
  }, [selectedAddressId, savedAddresses, newAddress]);

  const totals = useMemo(() => {
    const productTotal = unitPrice != null ? unitPrice * quantity : null;
    const shipping = selectedShipping.price;
    const subtotal = productTotal != null ? productTotal + shipping : null;
    const gst = subtotal != null ? subtotal * GST_RATE : null;
    const discount = 0;
    const grandTotal = subtotal != null ? subtotal + gst - discount : null;
    return { productTotal, shipping, gst, discount, grandTotal };
  }, [unitPrice, quantity, selectedShipping.price]);

  const scrollToStep = useCallback((stepId) => {
    if (stepId === "confirmation") return;
    setActiveStep(stepId);
    sectionRefs[stepId]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const adjustQuantity = (delta) => {
    setQuantity((q) => clampQuantity(q + delta));
  };

  const handleSaveNewAddress = () => {
    if (!newAddress.line.trim() || !newAddress.city.trim()) {
      toast.error("Please enter address and city");
      return;
    }
    const id = `custom-${Date.now()}`;
    const entry = { id, isDefault: false, ...newAddress };
    setSavedAddresses((prev) => [...prev, entry]);
    setSelectedAddressId(id);
    setShowNewAddress(false);
    toast.success("Address saved for this order");
  };

  const handlePlaceOrder = async () => {
    if (productEngagement?.has_submitted) {
      toast.info(getMarketplaceEngagementNotice(productEngagement).message);
      return;
    }
    if (!selectedAddress?.line?.trim()) {
      toast.error("Please select or add a delivery address");
      scrollToStep("address");
      return;
    }
    if (quantity < moqMin) {
      toast.error(`Minimum order quantity is ${moqMin} ${moqUnitLabel}`);
      return;
    }
    if (quantityMax != null && quantity > quantityMax) {
      toast.error(`Maximum available quantity is ${quantityMax} ${moqUnitLabel}`);
      return;
    }
    if (stockBelowMoq) {
      toast.error(`Available stock (${quantityMax} ${moqUnitLabel}) is below the MOQ (${moqMin} ${moqUnitLabel})`);
      return;
    }

    setSubmitting(true);
    try {
      const colorLabel = product?.colour_options?.find((c) => String(c.id) === String(selectedColor))?.label;
      const sizeParam = searchParams.get("size");
      const response = await b2bHubHomeService.placeCheckoutOrder(productId, {
        quantity,
        shippingMethod: selectedShipping.title,
        shippingCharge: selectedShipping.price,
        shippingAddress: {
          label: selectedAddress.label,
          line: selectedAddress.line,
          city: selectedAddress.city,
          state: selectedAddress.state,
          pincode: selectedAddress.pincode,
          country: selectedAddress.country,
        },
        gstNumber: gstNumber.trim() || null,
        companyName: companyName.trim() || null,
        poReference: poReference.trim() || null,
        specialInstructions: specialInstructions.trim() || null,
        selectedColor: colorLabel || null,
        selectedSize: sizeParam || null,
      });

      if (!response?.success) {
        throw new Error(response?.message || "Failed to place order");
      }

      const result = response?.data ?? response;
      const purchaseOrderId = result?.purchase_order_id;
      const paymentAmount = Number(result?.payment_amount);
      const poNumber = result?.po_number || `PO-${purchaseOrderId}`;
      const supplierName = result?.supplier_name || supplier?.company_name || "Supplier";

      setProductEngagement({
        has_submitted: true,
        rfq_id: result?.rfq_id ?? null,
        preferences: "buy-directly",
        engagement_type: "order",
      });
      toast.success("Purchase order created — proceed to payment");

      navigate("/payment/gateway", {
        state: {
          paymentData: {
            context: "marketplace_checkout",
            summaryTitle: `Marketplace payment · ${poNumber}`,
            summaryAmount: formatInr(paymentAmount),
            summaryLines: [
              `Product: ${product.title}`,
              `Supplier: ${supplierName}`,
              `PO: ${poNumber}`,
              `Quantity: ${quantity} ${moqUnitLabel}`,
            ],
          },
          marketplacePoPayment: {
            purchaseOrderId,
            poNumber,
            amount: paymentAmount,
            currency: result?.currency || "INR",
          },
          afterSuccessNavigate: "/po-status-tracking",
          afterSuccessState: { poId: purchaseOrderId },
        },
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to place order";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mpc-page">
        <B2BHubHomeHeader />
        <main className="mpc-main"><div className="mpc-loading">Loading checkout…</div></main>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mpc-page">
        <B2BHubHomeHeader />
        <main className="mpc-main">
          <p className="mpc-empty">Product not found.</p>
          <Link to="/marketplace/products">Back to marketplace</Link>
        </main>
      </div>
    );
  }

  const detailHref = `/marketplace/products/${productId}`;
  const categoryCrumb = breadcrumb[breadcrumb.length - 1]?.name || product.category_name || "Products";

  return (
    <div className="mpc-page">
      <B2BHubHomeHeader />
      <main className="mpc-main">
        <nav className="mpc-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span className="mpc-breadcrumb__sep">›</span>
          <Link to="/marketplace/products">Products</Link>
          <span className="mpc-breadcrumb__sep">›</span>
          <span>{categoryCrumb}</span>
          <span className="mpc-breadcrumb__sep">›</span>
          <Link to={detailHref}>Product Details</Link>
          <span className="mpc-breadcrumb__sep">›</span>
          <span className="mpc-breadcrumb__current">Checkout</span>
        </nav>

        <h1 className="mpc-page-title">Secure Checkout</h1>

        <div className="mpc-progress" role="navigation" aria-label="Checkout progress">
          {CHECKOUT_STEPS.map((step, idx) => {
            const stepIndex = CHECKOUT_STEPS.findIndex((s) => s.id === activeStep);
            const currentIndex = CHECKOUT_STEPS.findIndex((s) => s.id === step.id);
            const isComplete = currentIndex < stepIndex;
            const isActive = step.id === activeStep;
            return (
              <button
                key={step.id}
                type="button"
                className={`mpc-progress__step${isActive ? " is-active" : ""}${isComplete ? " is-complete" : ""}`}
                onClick={() => scrollToStep(step.id)}
                disabled={step.id === "confirmation"}
              >
                <span className="mpc-progress__dot">{isComplete ? <FaCheck aria-hidden /> : idx + 1}</span>
                <span className="mpc-progress__label">{step.label}</span>
              </button>
            );
          })}
        </div>

        {productEngagement?.has_submitted ? (
          (() => {
            const notice = getMarketplaceEngagementNotice(productEngagement);
            return (
              <p className="mpc-submitted-note">
                {notice.message}{" "}
                <Link to={notice.linkTo}>{notice.linkLabel}</Link>
              </p>
            );
          })()
        ) : null}

        <div className="mpc-layout">
          <div className="mpc-left">
            {/* Product Review */}
            <section className="mpc-card" ref={sectionRefs.review} id="checkout-review">
              <div className="mpc-card__head">
                <h2 className="mpc-card__title">Product Review</h2>
                <span className="mpc-card__step-badge">Step 1</span>
              </div>
              <div className="mpc-product">
                <div className="mpc-product__gallery">
                  <div className="mpc-product__stage">
                    {galleryImages.length ? (
                      <img src={resolveHubImage(galleryImages[activeImage])} alt={product.title} />
                    ) : (
                      <div style={{ display: "grid", placeItems: "center", height: "100%", color: "#94a3b8" }}>No image</div>
                    )}
                  </div>
                  {galleryImages.length > 1 ? (
                    <div className="mpc-product__thumbs">
                      {galleryImages.map((img, idx) => (
                        <button
                          key={`${img}-${idx}`}
                          type="button"
                          className={`mpc-product__thumb${activeImage === idx ? " is-active" : ""}`}
                          onClick={() => setActiveImage(idx)}
                        >
                          <img src={resolveHubImage(img)} alt="" loading="lazy" />
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div>
                  <h3 className="mpc-product__name">{product.title}</h3>
                  <div className="mpc-product__supplier-row">
                    <span className="mpc-product__supplier">{supplier?.company_name || "Supplier"}</span>
                    {supplier?.verified ? (
                      <span className="mpc-product__badge"><FaCheckCircle aria-hidden /> Verified Supplier</span>
                    ) : null}
                    <span className="mpc-product__rating"><FaStar aria-hidden /> 4.5</span>
                  </div>

                  <div className="mpc-product__grid">
                    <div className="mpc-product__field">
                      <span>MOQ</span>
                      <strong>{product.moq ? `${product.moq} ${moqUnitLabel}` : "—"}</strong>
                    </div>
                    <div className="mpc-product__field">
                      <span>Unit Price</span>
                      <strong>{product.rate_label || product.price_label || "—"}</strong>
                    </div>
                    <div className="mpc-product__field">
                      <span>Available Quantity</span>
                      <strong>{availableQty != null ? `${availableQty} ${moqUnitLabel}` : "On request"}</strong>
                    </div>
                    {supplier?.location ? (
                      <div className="mpc-product__field">
                        <span>Ships from</span>
                        <strong>{supplier.location}</strong>
                      </div>
                    ) : null}
                  </div>

                  {product.colour_options?.length ? (
                    <div>
                      <p className="mpc-product__qty-label" style={{ marginBottom: 8 }}>Color</p>
                      <div className="mpc-product__colors">
                        {product.colour_options.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            className={`mpc-product__color${String(selectedColor) === String(c.id) ? " is-active" : ""}`}
                            style={{ backgroundColor: c.hex }}
                            title={c.label}
                            onClick={() => setSelectedColor(c.id)}
                            aria-label={c.label}
                          />
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className="mpc-product__qty-row">
                    <span className="mpc-product__qty-label">Quantity</span>
                    <div className="mpc-product__qty-control">
                      <button type="button" onClick={() => adjustQuantity(-1)} disabled={quantity <= moqMin || stockBelowMoq} aria-label="Decrease">−</button>
                      <input
                        type="number"
                        min={moqMin}
                        max={quantityMax ?? undefined}
                        step={1}
                        value={quantity}
                        disabled={stockBelowMoq}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === "") return;
                          const n = parseInt(raw, 10);
                          if (!Number.isFinite(n)) return;
                          setQuantity(clampQuantity(n));
                        }}
                        onBlur={(e) => {
                          const n = parseInt(e.target.value, 10);
                          setQuantity(clampQuantity(Number.isFinite(n) ? n : moqMin));
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => adjustQuantity(1)}
                        disabled={stockBelowMoq || (quantityMax != null && quantity >= quantityMax)}
                        aria-label="Increase"
                      >
                        +
                      </button>
                    </div>
                    {quantityMax != null ? (
                      <p className="mpc-product__qty-hint">
                        Min {moqMin} · Max {quantityMax} {moqUnitLabel}
                      </p>
                    ) : (
                      <p className="mpc-product__qty-hint">Min {moqMin} {moqUnitLabel}</p>
                    )}
                    {stockBelowMoq ? (
                      <p className="mpc-product__stock-note">
                        Available stock is below MOQ — this product cannot be ordered right now.
                      </p>
                    ) : null}
                    <div className="mpc-product__line-total">
                      <span>Product Total</span>
                      <strong>{formatInr(totals.productTotal)}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Supplier */}
            <section className="mpc-card">
              <div className="mpc-card__head">
                <h2 className="mpc-card__title">Supplier Information</h2>
              </div>
              <div className="mpc-supplier">
                <div className="mpc-supplier__logo">
                  {supplier?.logo_url ? (
                    <img src={resolveHubImage(supplier.logo_url)} alt="" />
                  ) : (
                    (supplier?.company_name || "S").charAt(0).toUpperCase()
                  )}
                </div>
                <div className="mpc-supplier__body">
                  <h3 className="mpc-supplier__name">{supplier?.company_name || "Supplier"}</h3>
                  {supplier?.verified ? (
                    <span className="mpc-product__badge"><FaCheckCircle aria-hidden /> Verified Business</span>
                  ) : null}
                  <div className="mpc-supplier__stats">
                    <span>Response rate: <strong>98%</strong></span>
                    <span>On platform: <strong>3+ years</strong></span>
                    {supplier?.location ? (
                      <span><FaMapMarkerAlt aria-hidden /> {supplier.location}</span>
                    ) : null}
                  </div>
                  <div className="mpc-supplier__actions">
                    <Link to={detailHref} className="mpc-btn mpc-btn--outline mpc-btn--sm">Contact Supplier</Link>
                    <button type="button" className="mpc-btn mpc-btn--outline mpc-btn--sm" onClick={() => navigate(detailHref)}>
                      <FaComments aria-hidden /> Chat Now
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* Address */}
            <section className="mpc-card" ref={sectionRefs.address} id="checkout-address">
              <div className="mpc-card__head">
                <h2 className="mpc-card__title">Delivery Address</h2>
                <span className="mpc-card__step-badge">Step 2</span>
              </div>
              <div className="mpc-address-grid">
                {savedAddresses.map((addr) => (
                  <button
                    key={addr.id}
                    type="button"
                    className={`mpc-address-card${selectedAddressId === addr.id ? " is-selected" : ""}${addr.isDefault ? " is-default" : ""}`}
                    onClick={() => {
                      setSelectedAddressId(addr.id);
                      setShowNewAddress(false);
                    }}
                  >
                    <span className="mpc-address-card__tag">Default</span>
                    <p className="mpc-address-card__label">{addr.label}</p>
                    <p className="mpc-address-card__text">
                      {addr.line}
                      <br />
                      {[addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}
                      <br />
                      {addr.country}
                    </p>
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="mpc-btn mpc-btn--outline mpc-btn--sm"
                onClick={() => {
                  setShowNewAddress((v) => !v);
                  if (!showNewAddress) setSelectedAddressId("new");
                }}
              >
                + Add New Address
              </button>
              {showNewAddress ? (
                <div className="mpc-add-address">
                  <div className="mpc-add-address__grid">
                    <div>
                      <label htmlFor="addr-label">Label</label>
                      <input id="addr-label" value={newAddress.label} onChange={(e) => setNewAddress((p) => ({ ...p, label: e.target.value }))} />
                    </div>
                    <div>
                      <label htmlFor="addr-pincode">Pincode</label>
                      <input id="addr-pincode" value={newAddress.pincode} onChange={(e) => setNewAddress((p) => ({ ...p, pincode: e.target.value }))} />
                    </div>
                    <div className="mpc-add-address__full">
                      <label htmlFor="addr-line">Address</label>
                      <textarea id="addr-line" rows={2} value={newAddress.line} onChange={(e) => setNewAddress((p) => ({ ...p, line: e.target.value }))} />
                    </div>
                    <div>
                      <label htmlFor="addr-city">City</label>
                      <input id="addr-city" value={newAddress.city} onChange={(e) => setNewAddress((p) => ({ ...p, city: e.target.value }))} />
                    </div>
                    <div>
                      <label htmlFor="addr-state">State</label>
                      <input id="addr-state" value={newAddress.state} onChange={(e) => setNewAddress((p) => ({ ...p, state: e.target.value }))} />
                    </div>
                  </div>
                  <button type="button" className="mpc-btn mpc-btn--primary mpc-btn--sm" onClick={handleSaveNewAddress}>Save Address</button>
                </div>
              ) : null}
            </section>

            {/* Shipping */}
            <section className="mpc-card" ref={sectionRefs.shipping} id="checkout-shipping">
              <div className="mpc-card__head">
                <h2 className="mpc-card__title">Shipping Method</h2>
                <span className="mpc-card__step-badge">Step 3</span>
              </div>
              <div className="mpc-shipping-options">
                {SHIPPING_OPTIONS.map((opt) => (
                  <label
                    key={opt.id}
                    className={`mpc-shipping-option${shippingMethod === opt.id ? " is-selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="shipping"
                      value={opt.id}
                      checked={shippingMethod === opt.id}
                      onChange={() => setShippingMethod(opt.id)}
                    />
                    <div className="mpc-shipping-option__body">
                      <p className="mpc-shipping-option__title"><FaTruck aria-hidden /> {opt.title}</p>
                      <p className="mpc-shipping-option__meta">
                        {opt.description}
                        <br />
                        Est. delivery: <strong>{addDaysLabel(opt.etaDays)}</strong>
                      </p>
                    </div>
                    <span className="mpc-shipping-option__price">
                      {opt.price === 0 ? "Free" : formatInr(opt.price)}
                    </span>
                  </label>
                ))}
              </div>
            </section>

            {/* GST & Payment details */}
            <section className="mpc-card" ref={sectionRefs.payment} id="checkout-payment">
              <div className="mpc-card__head">
                <h2 className="mpc-card__title">GST &amp; Business Details</h2>
                <span className="mpc-card__step-badge">Step 4</span>
              </div>
              <div className="mpc-form-grid">
                <div>
                  <label htmlFor="gst">GST Number</label>
                  <input id="gst" value={gstNumber} onChange={(e) => setGstNumber(e.target.value)} placeholder="22AAAAA0000A1Z5" />
                </div>
                <div>
                  <label htmlFor="billing-company">Company Name</label>
                  <input id="billing-company" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                </div>
                <div className="mpc-form-grid__full">
                  <label htmlFor="po-ref">Purchase Order Reference</label>
                  <input id="po-ref" value={poReference} onChange={(e) => setPoReference(e.target.value)} placeholder="Optional internal PO number" />
                </div>
                <div className="mpc-form-grid__full">
                  <label htmlFor="instructions">Special Instructions</label>
                  <textarea
                    id="instructions"
                    rows={4}
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    placeholder="Packaging, delivery window, invoicing notes…"
                  />
                </div>
              </div>
            </section>
          </div>

          {/* Sticky summary */}
          <aside className="mpc-summary">
            <div className="mpc-summary__card">
              <h2 className="mpc-summary__title">Order Summary</h2>
              <div className="mpc-summary__rows">
                <div className="mpc-summary__row">
                  <span>Product Total ({quantity} × {formatInr(unitPrice)})</span>
                  <strong>{formatInr(totals.productTotal)}</strong>
                </div>
                <div className="mpc-summary__row">
                  <span>Shipping</span>
                  <strong>{selectedShipping.price === 0 ? "Free" : formatInr(totals.shipping)}</strong>
                </div>
                <div className="mpc-summary__row">
                  <span>GST (18%)</span>
                  <strong>{formatInr(totals.gst)}</strong>
                </div>
                {totals.discount > 0 ? (
                  <div className="mpc-summary__row mpc-summary__row--discount">
                    <span>Discount</span>
                    <strong>−{formatInr(totals.discount)}</strong>
                  </div>
                ) : null}
                <div className="mpc-summary__row mpc-summary__row--total">
                  <span>Grand Total</span>
                  <strong>{formatInr(totals.grandTotal)}</strong>
                </div>
              </div>

              <div className="mpc-summary__actions">
                <button
                  type="button"
                  className="mpc-btn mpc-btn--primary mpc-btn--block"
                  onClick={handlePlaceOrder}
                  disabled={submitting || productEngagement?.has_submitted || stockBelowMoq}
                >
                  {submitting ? "Creating purchase order…" : "Place Order & Pay"}
                </button>
              </div>

              <div className="mpc-summary__trust">
                <div className="mpc-summary__trust-item"><FaShieldAlt aria-hidden /> Secure Transaction</div>
                {supplier?.verified ? (
                  <div className="mpc-summary__trust-item"><FaCheckCircle aria-hidden /> Verified Supplier</div>
                ) : null}
                <div className="mpc-summary__trust-item"><FaCheck aria-hidden /> Trade Protection</div>
                <div className="mpc-summary__trust-item"><FaPaperPlane aria-hidden /> GST Invoice Available</div>
              </div>
            </div>
          </aside>
        </div>

        {related.length ? (
          <ProductCarousel title="Similar Products" items={related} trackId="mpc-similar-track" />
        ) : null}

        {recentlyViewed.length ? (
          <ProductCarousel title="Recently Viewed Products" items={recentlyViewed} trackId="mpc-recent-track" />
        ) : null}

        {supplier ? (
          <section className="mpc-carousel-section">
            <div className="mpc-carousel-section__head">
              <h2>Recommended Suppliers</h2>
            </div>
            <div className="mpc-carousel-section__track">
              <div className="mpc-supplier-card">
                <p className="mpc-supplier-card__name">{supplier.company_name}</p>
                <p className="mpc-supplier-card__meta">
                  {supplier.location || "India"} · Verified B2B supplier on TROCANET
                </p>
                <Link to={detailHref} className="mpc-btn mpc-btn--outline mpc-btn--sm">View Profile</Link>
              </div>
            </div>
          </section>
        ) : null}
      </main>
    </div>
  );
}
